import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { take } from 'rxjs';

// eslint-disable-next-line boundaries/element-types
import { injectCountryList } from '@core/country-data';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import type { Country } from '@core/locations-types';
import { CompanyVerificationDataService } from '@core/tellent-company-verification-logic';
import { CompanyVerificationRequestPayload } from '@core/tellent-company-verification-types';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  FormFieldComponent,
  IconHelpComponent,
  InputComponent,
  LabelComponent,
  NotificationsController,
  RtTooltip,
  SelectComponent,
  SelectOption,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

@Component({
  selector: 'tlc-company-verification-dialog',
  templateUrl: './company-verification-dialog.component.html',
  styleUrls: ['./company-verification-dialog.component.less'],
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    ButtonLoadingDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    FormFieldComponent,
    IconHelpComponent,
    InputComponent,
    LabelComponent,
    SelectComponent,
    TranslatePipe,
    RtTooltip,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyVerificationDialogComponent implements DialogComponentI<
  any,
  { success: boolean }
> {
  private readonly fb = inject(FormBuilder);
  private readonly untilDestroyed = injectUntilDestroyed();
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly dialogRef = inject(DialogRef);
  private readonly companyVerificationDataService = inject(CompanyVerificationDataService);
  private readonly notificationService = inject(NotificationsController);
  private readonly t = injectTranslate();

  public form: FormGroup<{
    companyName: FormControl<string>;
    companyWebsite: FormControl<string>;
    companyAddress: FormControl<string>;
    companyTaxNumber: FormControl<string>;
    companyCountryCode: FormControl<Country['name']>;
  }>;

  public countries: Array<SelectOption<Country['name']>> = injectCountryList().map(country => ({
    label: country.name,
    value: country.countryCode.toString(),
  }));

  public loading = signal<boolean>(false);

  public setDialogData(): void {
    //
  }

  public ngOnInit(): void {
    this.form = this.fb.nonNullable.group({
      companyName: ['', [Validators.required]],
      companyWebsite: ['', [Validators.required]],
      companyAddress: ['', [Validators.required]],
      companyTaxNumber: ['', [Validators.required]],
      companyCountryCode: ['', [Validators.required]],
    });

    this.form.valueChanges.pipe(this.untilDestroyed).subscribe(() => {
      this.cdRef.detectChanges();
    });
  }

  public continue(): void {
    const payload = { ...this.form.value };
    this.loading.set(true);
    this.companyVerificationDataService
      .verify(<CompanyVerificationRequestPayload>payload)
      .pipe(take(1))
      .subscribe(result => {
        this.loading.set(false);
        if (result.success) {
          this.notificationService.success(this.t('tlc.company_verification.request_sent'));
          this.dialogRef.close({ success: true });
        }
      });
  }

  public cancel(): void {
    this.dialogRef.close();
  }
}
